import React, { useState, useEffect, useCallback } from 'react';
import { Search, Phone, ShoppingBag, TrendingUp, Star, X, Users, Crown, RefreshCw } from 'lucide-react';
import { supabase } from '../../utils/supabase';

const statusStyles = {
  active: 'bg-blue-50 text-blue-600',
  vip: 'bg-yellow-50 text-yellow-700',
  inactive: 'bg-gray-100 text-gray-500',
};

const statusEmoji = {
  active: '🟢',
  vip: '⭐',
  inactive: '😴',
};

const orderStatusStyles = {
  pending: 'bg-orange-100 text-orange-700',
  confirmed: 'bg-blue-100 text-blue-700',
  ready: 'bg-green-100 text-green-700',
  delivered: 'bg-gray-100 text-gray-600',
};

const getCustomerStatus = (totalOrders, createdAt) => {
  if (totalOrders >= 10) return 'vip';
  const daysSince = (Date.now() - new Date(createdAt)) / (1000 * 60 * 60 * 24);
  if (daysSince > 14 && totalOrders === 0) return 'inactive';
  return 'active';
};

const timeAgo = (dateStr) => {
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)} days ago`;
  return new Date(dateStr).toLocaleDateString();
};

export default function Customers() {
  const [customers, setCustomers] = useState([]);
  const [customerOrders, setCustomerOrders] = useState({});
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [promoMessage, setPromoMessage] = useState('');
  const [restaurantId, setRestaurantId] = useState(null); // eslint-disable-line

  const fetchCustomers = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: rest } = await supabase
        .from('restaurants')
        .select('id')
        .eq('email', user.email)
        .single();

      if (!rest) return;
      setRestaurantId(rest.id);

      const { data: customersData } = await supabase
        .from('customers')
        .select('*')
        .eq('restaurant_id', rest.id)
        .order('total_spent', { ascending: false });

      setCustomers(customersData || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  const fetchCustomerOrders = async (customerId, phone) => {
    if (customerOrders[customerId]) return;
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { data: rest } = await supabase
        .from('restaurants')
        .select('id')
        .eq('email', user.email)
        .single();

      const { data: orders } = await supabase
        .from('orders')
        .select('*')
        .eq('restaurant_id', rest.id)
        .eq('customer_phone', phone)
        .order('created_at', { ascending: false });

      setCustomerOrders(prev => ({ ...prev, [customerId]: orders || [] }));
    } catch (err) {
      console.error(err);
    }
  };

  const handleSelectCustomer = (customer) => {
    setSelected(customer);
    setPromoMessage('');
    fetchCustomerOrders(customer.id, customer.phone);
  };

  const enrichedCustomers = customers.map(c => ({
    ...c,
    status: getCustomerStatus(c.total_orders, c.created_at),
  }));

  const vipCount = enrichedCustomers.filter(c => c.status === 'vip').length;
  const totalRevenue = customers.reduce((a, c) => a + (c.total_spent || 0), 0);
  const totalOrders = customers.reduce((a, c) => a + (c.total_orders || 0), 0);
  const avgOrderValue = totalOrders > 0 ? Math.round(totalRevenue / totalOrders) : 0;

  const filtered = enrichedCustomers.filter(c => {
    const matchSearch =
      (c.name || '').toLowerCase().includes(search.toLowerCase()) ||
      (c.phone || '').includes(search);
    const matchFilter = filter === 'all' || c.status === filter;
    return matchSearch && matchFilter;
  });

  const handleSendPromo = () => {
    if (!selected || !promoMessage.trim()) return;
    const phone = selected.phone.replace(/\D/g, '');
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(promoMessage)}`, '_blank');
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-3 gap-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-white rounded-2xl border border-gray-100 p-4 animate-pulse h-20" />
          ))}
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 p-6 animate-pulse h-64" />
      </div>
    );
  }

  return (
    <div className="space-y-5">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-800">Customers CRM</h2>
          <p className="text-sm text-gray-400 mt-0.5">{customers.length} total customers</p>
        </div>
        <div className="flex items-center gap-2">
          {vipCount > 0 && (
            <div className="bg-yellow-50 border border-yellow-100 text-yellow-700 text-xs font-semibold px-3 py-1.5 rounded-xl flex items-center gap-1">
              <Crown size={12} /> {vipCount} VIP
            </div>
          )}
          <button
            onClick={() => fetchCustomers(true)}
            disabled={refreshing}
            className="p-2 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition"
          >
            <RefreshCw size={16} className={`text-gray-500 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Search + Filter */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name or phone..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary bg-white"
          />
        </div>
        <div className="flex gap-2">
          {[
            { key: 'all', label: 'All' },
            { key: 'vip', label: '⭐ VIP' },
            { key: 'active', label: '🟢 Active' },
            { key: 'inactive', label: '😴 Inactive' },
          ].map(f => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition
                ${filter === f.key ? 'bg-primary text-white' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Total Customers', value: customers.length, icon: Users, color: 'text-blue-500 bg-blue-50' },
          { label: 'Total Revenue', value: `SAR ${totalRevenue.toLocaleString()}`, icon: TrendingUp, color: 'text-green-500 bg-green-50' },
          { label: 'Avg. Order Value', value: `SAR ${avgOrderValue}`, icon: ShoppingBag, color: 'text-purple-500 bg-purple-50' },
        ].map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${color}`}>
              <Icon size={18} />
            </div>
            <div className="min-w-0">
              <p className="text-xs text-gray-500 truncate">{label}</p>
              <p className="font-bold text-gray-800 truncate">{value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Customers Table */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {filtered.length === 0 ? (
          <div className="p-16 text-center">
            <div className="w-16 h-16 bg-gray-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Users size={28} className="text-gray-200" />
            </div>
            <p className="text-gray-400 font-medium">No customers yet</p>
            <p className="text-gray-300 text-sm mt-1">Customers appear automatically when they order via WhatsApp</p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50">
                <th className="text-left text-xs font-semibold text-gray-400 px-6 py-3 uppercase tracking-wide">Customer</th>
                <th className="text-left text-xs font-semibold text-gray-400 px-6 py-3 uppercase tracking-wide">Phone</th>
                <th className="text-left text-xs font-semibold text-gray-400 px-6 py-3 uppercase tracking-wide">Orders</th>
                <th className="text-left text-xs font-semibold text-gray-400 px-6 py-3 uppercase tracking-wide">Spent</th>
                <th className="text-left text-xs font-semibold text-gray-400 px-6 py-3 uppercase tracking-wide">Joined</th>
                <th className="text-left text-xs font-semibold text-gray-400 px-6 py-3 uppercase tracking-wide">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filtered.map(customer => (
                <tr
                  key={customer.id}
                  onClick={() => handleSelectCustomer(customer)}
                  className="hover:bg-gray-50 cursor-pointer transition"
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${customer.status === 'vip' ? 'bg-yellow-50' : 'bg-primary/10'}`}>
                        {customer.status === 'vip'
                          ? <Star size={16} className="text-yellow-500 fill-yellow-500" />
                          : <span className="text-primary font-bold text-sm">{(customer.phone || '?').slice(-2)}</span>
                        }
                      </div>
                      <div>
                        <p className="font-medium text-gray-800 text-sm">
                          {customer.name && customer.name !== customer.phone ? customer.name : 'Customer'}
                        </p>
                        <p className="text-xs text-gray-400">#{customer.id.slice(0, 6)}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500 font-mono">+{customer.phone}</td>
                  <td className="px-6 py-4 text-sm font-bold text-gray-800">{customer.total_orders}</td>
                  <td className="px-6 py-4 text-sm font-bold text-gray-800">SAR {customer.total_spent || 0}</td>
                  <td className="px-6 py-4 text-sm text-gray-400">{timeAgo(customer.created_at)}</td>
                  <td className="px-6 py-4">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium capitalize ${statusStyles[customer.status]}`}>
                      {statusEmoji[customer.status]} {customer.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Customer Detail Drawer */}
      {selected && (
        <div className="fixed inset-0 bg-black/50 z-50 flex justify-end" onClick={() => setSelected(null)}>
          <div className="bg-white w-full max-w-md h-full overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>

            {/* Header */}
            <div className={`p-6 border-b border-gray-100 ${selected.status === 'vip' ? 'bg-yellow-50' : ''}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${selected.status === 'vip' ? 'bg-yellow-100' : 'bg-primary/10'}`}>
                    {selected.status === 'vip'
                      ? <Star size={24} className="text-yellow-500 fill-yellow-500" />
                      : <span className="text-primary font-bold text-2xl">{(selected.phone || '?').slice(-2)}</span>
                    }
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-bold text-gray-800">
                        {selected.name && selected.name !== selected.phone ? selected.name : 'Customer'}
                      </h3>
                      {selected.status === 'vip' && (
                        <span className="bg-yellow-100 text-yellow-700 text-xs font-bold px-2 py-0.5 rounded-full">⭐ VIP</span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 font-mono">+{selected.phone}</p>
                  </div>
                </div>
                <button onClick={() => setSelected(null)} className="p-2 hover:bg-gray-100 rounded-xl">
                  <X size={18} className="text-gray-500" />
                </button>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-3 p-6 border-b border-gray-100">
              {[
                { label: 'Orders', value: selected.total_orders },
                { label: 'Total Spent', value: `SAR ${selected.total_spent || 0}` },
                { label: 'Since', value: timeAgo(selected.created_at) },
              ].map(({ label, value }) => (
                <div key={label} className="bg-gray-50 rounded-xl p-3 text-center">
                  <p className="text-xs text-gray-500 mb-1">{label}</p>
                  <p className="font-bold text-gray-800 text-sm">{value}</p>
                </div>
              ))}
            </div>

            {/* Order History */}
            <div className="p-6">
              <h4 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <ShoppingBag size={16} className="text-gray-400" />
                Order History
              </h4>
              <div className="space-y-3">
                {!customerOrders[selected.id] ? (
                  <div className="animate-pulse space-y-2">
                    {[1, 2].map(i => <div key={i} className="h-16 bg-gray-100 rounded-xl" />)}
                  </div>
                ) : customerOrders[selected.id].length === 0 ? (
                  <div className="text-center py-6">
                    <ShoppingBag size={24} className="text-gray-200 mx-auto mb-2" />
                    <p className="text-gray-400 text-sm">No orders yet</p>
                  </div>
                ) : (
                  customerOrders[selected.id].map(order => {
                    const items = Array.isArray(order.items)
                      ? order.items.map(i => `${i.name} x${i.qty}`).join(', ')
                      : '';
                    return (
                      <div key={order.id} className="border border-gray-100 rounded-xl p-4 hover:bg-gray-50 transition">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-semibold text-gray-800 text-sm">#{order.id.slice(0, 8)}</span>
                          <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${orderStatusStyles[order.status] || 'bg-gray-100 text-gray-600'}`}>
                            {order.status}
                          </span>
                        </div>
                        {items && <p className="text-xs text-gray-500 mb-2">{items}</p>}
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-400">{timeAgo(order.created_at)}</span>
                          <span className="font-bold text-gray-800 text-sm">SAR {order.total}</span>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {/* Send Promo */}
              <div className="mt-6 bg-green-50 rounded-2xl p-4">
                <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <Phone size={16} className="text-green-600" />
                  Send WhatsApp Promo
                </h4>
                <textarea
                  placeholder={`Hi! Special offer for you today... 🎉`}
                  value={promoMessage}
                  onChange={e => setPromoMessage(e.target.value)}
                  className="w-full border border-green-200 bg-white rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-green-400 resize-none"
                  rows={3}
                />
                <button
                  onClick={handleSendPromo}
                  disabled={!promoMessage.trim()}
                  className="mt-3 w-full bg-green-500 text-white py-2.5 rounded-xl text-sm font-medium hover:bg-green-600 transition flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  <Phone size={16} /> Send via WhatsApp
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}